// components/Footer.jsx
import React from "react";
import { Facebook, Twitter, Instagram, Youtube, Linkedin } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Unified function to scroll to campaigns
  const scrollToCampaigns = () => {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      const campaignsSection = document.getElementById('campaigns');
      if (campaignsSection) {
        campaignsSection.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  // Handle campaigns link click
  const handleCampaignsClick = (e) => {
    e.preventDefault();
    
    if (location.pathname === '/') {
      // Already on home page, just scroll
      scrollToCampaigns();
    } else {
      // Navigate to home page and then scroll
      navigate('/');
      scrollToCampaigns();
    }
  };

  // Handle Donate Now button click
  const handleDonateClick = (e) => {
    e.preventDefault();
    
    if (location.pathname === '/') {
      scrollToCampaigns();
    } else {
      navigate('/');
      scrollToCampaigns();
    }
  };

  const footerLinks = {
    "Quick Links": [
      { label: "Home", path: "/" },
      { 
        label: "Campaigns", 
        path: "#", 
        onClick: handleCampaignsClick,
        isButton: true 
      },
      { label: "Start Fundraiser", path: "/start-fundraiser" },
    ],
    "Account": [
      { label: "Sign In", path: "/signin" },
      { label: "Register", path: "/signin" },
    ]
  };

  const socialIcons = [
    { IconComponent: Facebook, label: "Facebook" },
    { IconComponent: Twitter, label: "Twitter" },
    { IconComponent: Instagram, label: "Instagram" },
    { IconComponent: Youtube, label: "YouTube" },
    { IconComponent: Linkedin, label: "LinkedIn" },
  ];

  return (
    <>
      {/* Simple CTA Banner */}
      <div className="bg-[#009e9a] py-4 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="text-white font-medium">
                Start making a difference today
              </p>
            </div>
            <div className="flex gap-3">
              <Link 
                to="/start-fundraiser" 
                className="bg-white text-[#009e9a] font-medium px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Start Fundraiser
              </Link>
              
              <button
                onClick={handleDonateClick}
                className="border border-white text-white font-medium px-6 py-2 rounded-lg hover:bg-white/20 transition-colors"
              >
                Donate Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <footer className="bg-[#007d79] py-8">
        <div className="max-w-7xl mx-auto px-4">
          {/* Top Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pb-8">
            {/* Brand Section */}
            <div className="space-y-4">
              <Link to="/" className="inline-block">
                <div className="flex items-center gap-3">
                  <div>
                    <span className="text-white font-bold text-3xl">
                      DonatePe
                    </span>
                    <p className="text-[#d4f0f0] text-sm">
                      Compassionate giving
                    </p>
                  </div>
                </div>
              </Link>
              
              <p className="text-[#d4f0f0] text-sm">
                Empowering people to create positive change through secure and transparent giving.
              </p>
            </div>

            {/* Links */}
            <div className="grid grid-cols-2 gap-8">
              {Object.entries(footerLinks).map(([category, links]) => (
                <div key={category} className="space-y-3">
                  <h4 className="font-bold text-white text-base mb-3">
                    {category}
                  </h4>
                  <div className="space-y-2">
                    {links.map((link) => (
                      link.isButton ? (
                        <button
                          key={link.label}
                          onClick={link.onClick}
                          className="text-[#d4f0f0] hover:text-white text-sm transition-colors block w-full text-left"
                        >
                          {link.label}
                        </button>
                      ) : (
                        <Link
                          key={link.label}
                          to={link.path}
                          className="text-[#d4f0f0] hover:text-white text-sm transition-colors block"
                        >
                          {link.label}
                        </Link>
                      )
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Social Media */}
            <div className="space-y-4">
              <h4 className="font-bold text-white text-base">Connect with us</h4>
              <div className="flex gap-3">
                {socialIcons.map(({ IconComponent, label }) => (
                  <a
                    key={label}
                    href="#"
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-[#009e9a] text-white hover:bg-[#00b7b3] transition-colors"
                    aria-label={label}
                    onClick={(e) => e.preventDefault()}
                  >
                    <IconComponent className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-[#009e9a] pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <p className="text-[#d4f0f0] text-sm">
                  © {new Date().getFullYear()} DonatePe. All rights reserved.
                </p>
                <p className="text-[#b0e0e0] text-xs mt-1">
                  A compassionate giving platform
                </p>
              </div>
              
              <div className="flex gap-4">
                <Link to="#" className="text-[#d4f0f0] hover:text-white text-sm transition-colors">
                  Privacy
                </Link>
                <Link to="#" className="text-[#d4f0f0] hover:text-white text-sm transition-colors">
                  Terms
                </Link>
                <Link to="#" className="text-[#d4f0f0] hover:text-white text-sm transition-colors">
                  Contact
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;